// Public surface for the auth module.
//
// Exception to the "modules export only routes" rule: auth is consumed by
// other modules (signUp, invest, AppLayout) for token state and on-demand
// privacy/T&C content. Those public hooks and helpers are exposed here.

export { authRoutes } from './authRoute'
export { useAuthStore } from './store/authStore'

// Auth shell chrome shared with the onboarding wizard. The auth/signUp screens
// and the post-signup onboarding wizard render the same showcase panel + top
// header, and onboarding's OTP step reuses auth's OtpVerificationForm.
export { default as AuthShowcasePanel } from './components/AuthShowcasePanel.vue'
export { default as AuthTopHeader } from './components/AuthTopHeader.vue'
export { default as OtpVerificationForm } from './components/OtpVerificationForm.vue'
export { default as TrustDossier } from './components/TrustDossier.vue'
export { default as AuthTrustChips } from './components/AuthTrustChips.vue'

// Shared-endpoint hooks — `/verify-otp` and `/resend-otp` serve both the
// login flow (`VerifyOtpContainer`) and the onboarding sign-up flow
// (`SignUpOtpContainer`). Per architecture-principles.md §10's documented
// exception, the owning module re-exports the hook rather than the consumer
// duplicating the endpoint definition.
export { useVerifyOtpMutation } from './api/mutations/useVerifyOtpMutation'
export { useResendOtpMutation } from './api/mutations/useResendOtpMutation'

// UAE Pass sign-up completion — the confirm screen lives in the onboarding
// module (signUp flow) but the endpoint is owned here, so the hook and the
// prefill type are re-exported for that consumer.
export { useUaePassRegisterMutation } from './api/mutations/useUaePassRegisterMutation'
export type { UaePassSignupPrefill } from './types/authTypes'

// UAE Pass entry point (authorize URL → redirect). Owned here but also consumed
// by the onboarding sign-up screen's "Continue with UAE Pass" button, so the
// hook is re-exported per the documented public-surface exception.
export { useUaePassAuthorizeUrlMutation } from './api/mutations/useUaePassAuthorizeUrlMutation'

// Enable / disable UAE Pass as a login method from Account Settings. Owned here
// (auth endpoints) but consumed by the account module's Settings screen, so the
// hooks are re-exported per the documented public-surface exception.
export { useUaePassLinkAuthorizeUrlMutation } from './api/mutations/useUaePassLinkAuthorizeUrlMutation'
export { useUaePassDisableMutation } from './api/mutations/useUaePassDisableMutation'

// Authoritative enabled-login-method state — the source of truth for the
// Account Settings toggles. Re-exported alongside the enable/disable hooks and
// the query key, which the UAE Pass callback invalidates after a successful link.
export { useLoginMethodsQuery } from './api/queries/useLoginMethodsQuery'
export { authKeys } from './api/authQueryKeys'
export { LOGIN_METHOD } from './types/authTypes'

// Cached privacy/T&C content — owned here (auth endpoints) but rendered by the
// account module's policy detail screen, so the hooks are re-exported per the
// documented public-surface exception.
export { usePrivacyPolicyQuery } from './api/queries/usePrivacyPolicyQuery'
export { useTermsAndConditionsQuery } from './api/queries/useTermsAndConditionsQuery'

// Imperative helpers — used by legacy modules that need a Promise-returning
// fetcher (privacy/T&C drawers in signUp + invest). These do NOT use the
// query cache; usePrivacyPolicyQuery / useTermsAndConditionsQuery are the cached path.
import { getPrivacyPolicy, getTermsAndConditions } from './api/authEndpoints'

// Server-side logout. AppLayout.handleLogout wraps this in try/finally so
// the local session is cleared even on network failure. Exposed as an
// imperative helper (not a query/mutation hook) because the caller
// orchestrates store cleanup + cache clearing + router navigation in one
// sequence and doesn't need TanStack's reactive isPending/error surface
// for this one-shot fire-once call.
export { logout } from './api/authEndpoints'
import {
  parsePrivacyPolicyResponse,
  parseTermsAndConditionsResponse,
} from './api/authResponseParser'
import type { PrivacyPolicyResponse, TermsAndConditionsResponse } from './types/authTypes'

export async function fetchPrivacyPolicy(): Promise<PrivacyPolicyResponse> {
  return getPrivacyPolicy().then(parsePrivacyPolicyResponse)
}

export async function fetchTermsAndConditions(): Promise<TermsAndConditionsResponse> {
  return getTermsAndConditions().then(parseTermsAndConditionsResponse)
}
